import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { RevealText, Reveal } from "@/components/site/Reveal";
import { LotusMark } from "@/components/site/Brand";
import { CLINIC, CLINIC_HOURS, IMAGES } from "@/lib/data";
import { ArrowDownRight, Clock } from "lucide-react";

export default function Hero({ content }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} className="relative min-h-screen grain overflow-hidden pt-28 pb-16" data-testid="hero">
      {/* floating decorative blobs */}
      <motion.div style={{ y: yBlob }} className="absolute -top-24 -right-24 w-[36rem] h-[36rem] rounded-full bg-blush-pale/60 blur-[80px]" />
      <div className="absolute top-1/3 -left-32 w-[28rem] h-[28rem] rounded-full bg-clay/20 blur-[90px]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start min-h-[78vh]">
        <div className="lg:col-span-7 z-10">
          <Reveal delay={0.3}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blush/40 bg-white/50 backdrop-blur-sm mb-6">
              <span className="w-2 h-2 rounded-full bg-blush-dusty animate-pulse" />
              <span className="font-sans text-xs tracking-[0.25em] uppercase text-olive-sage">
                Gynaecology · Obstetrics · Fertility
              </span>
            </div>
          </Reveal>

          <h1 className="font-serif font-light text-olive-deep leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
            <RevealText delay={0.4} lines={["A gentle new", "beginning for"]} />
            <RevealText delay={0.65} lines={["women's health"]} lineClass="italic text-blush-dusty" />
          </h1>

          <Reveal delay={0.3} y={20}>
            <p className="mt-6 font-sans text-base sm:text-lg text-olive-sage max-w-xl leading-relaxed">
              Asmi is a boutique women's health & infertility practice by <span className="text-olive-deep">Dr. Shivani Jain</span>,
              in St. Inez, Goa. Compassionate, evidence-based care — from your first question to your happiest new chapter.
            </p>
          </Reveal>

          <Reveal delay={0.45} y={20}>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <button onClick={() => go("#consultations")} data-testid="hero-book-btn"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-olive-deep text-cream font-sans text-sm hover:bg-blush-dusty transition-colors duration-300">
                Book a consultation
                <ArrowDownRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
              </button>
              <button onClick={() => go("#notify")} data-testid="hero-notify-btn"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-olive-sage/40 text-olive-deep font-sans text-sm hover:border-olive-deep hover:bg-white/40 transition-colors duration-300">
                Get in touch
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.6} y={20}>
            <div className="mt-8 max-w-md rounded-3xl border border-blush/40 bg-white/55 backdrop-blur-md p-5 shadow-[0_12px_40px_rgba(47,51,43,0.06)]" data-testid="clinic-hours">
              <div className="flex items-center gap-2.5 mb-3">
                <Clock className="w-4 h-4 text-blush-dusty" />
                <span className="font-sans text-xs tracking-[0.28em] uppercase text-olive-sage">Clinic Hours</span>
              </div>
              <dl className="divide-y divide-olive-sage/12">
                {CLINIC_HOURS.map((h) => (
                  <div key={h.day} className="flex items-baseline justify-between gap-6 py-1.5" data-testid={`hours-${h.day.toLowerCase()}`}>
                    <dt className="font-sans text-sm text-olive-deep shrink-0 w-24">{h.day}</dt>
                    <dd className="font-sans text-[13px] text-olive-sage text-right leading-snug">{h.time}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>

        {/* decorative brand panel (photo to be added later) */}
        <div className="lg:col-span-5 relative z-10">
          <motion.div style={{ opacity }} className="relative">
            <motion.div style={{ y: yImg }} className="arch overflow-hidden shadow-[0_30px_80px_rgba(47,51,43,0.14)] aspect-[3/4] grain bg-gradient-to-b from-blush-pale via-beige/50 to-clay/25 flex items-center justify-center p-10">
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_asmi-gynae/artifacts/slu2xfkj_asmi%20logo%20finak.jpeg"
                alt="Asmi Women Health & Infertility Clinic"
                className="w-56 sm:w-64 h-auto object-contain animate-float mix-blend-multiply"
                data-testid="hero-logo"
              />
            </motion.div>
            <div className="absolute -top-6 -right-6 px-5 py-3 rounded-2xl bg-white/85 backdrop-blur-md shadow-lg border border-blush/30">
              <p className="font-serif text-xl text-olive-deep leading-none">Dr. Shivani Jain</p>
              <p className="font-sans text-[10px] tracking-widest uppercase text-olive-sage mt-1">MBBS · DNB · MD (AIIMS)</p>
            </div>
          </motion.div>
        </div>
      </div>

      <Reveal delay={1.3} className="relative max-w-7xl mx-auto px-6 lg:px-10 mt-6">
        <p className="font-sans text-xs tracking-[0.25em] uppercase text-olive-sage/70">{CLINIC.specialities}</p>
      </Reveal>
    </section>
  );
}
